import { z } from 'zod';
import { validateCuit } from '../../utils/validation';
import { todayLocal } from '../../utils/format';

const hoyIso = () => todayLocal();

/**
 * Schema Zod del formulario de Afiliado.
 * Compartido por todos los tabs.
 *
 * Reforzado en deuda #031:
 *   - trim en todos los strings libres
 *   - email: lowercase
 *   - fecha_nacimiento: no futura, no anterior a 1900
 *   - fecha_alta_sistema: no futura
 *   - mensajes con tildes en español
 */
export const afiliadoFormSchema = z.object({
  cuil: z
    .string({ required_error: 'CUIL es requerido' })
    .trim()
    .min(1, 'CUIL es requerido')
    .refine((v) => validateCuit(v), 'CUIL inválido (dígito verificador AFIP no coincide)'),
  tipo_documento: z.enum(['DNI', 'LE', 'CE']),
  numero_documento: z
    .string({ required_error: 'Número de documento es requerido' })
    .trim()
    .min(6, 'Número de documento muy corto (mínimo 6 dígitos)')
    .max(15, 'Número de documento muy largo (máximo 15 caracteres)'),
  apellido: z
    .string({ required_error: 'Apellido es requerido' })
    .trim()
    .min(2, 'Apellido debe tener al menos 2 caracteres')
    .max(100, 'Apellido no puede superar 100 caracteres'),
  nombre: z
    .string({ required_error: 'Nombre es requerido' })
    .trim()
    .min(2, 'Nombre debe tener al menos 2 caracteres')
    .max(100, 'Nombre no puede superar 100 caracteres'),
  sexo: z.enum(['M', 'F', 'X']).nullable().optional(),
  fecha_nacimiento: z
    .string()
    .refine(
      (v) => v === '' || v <= hoyIso(),
      'Fecha de nacimiento no puede ser futura',
    )
    .refine(
      (v) => v === '' || v >= '1900-01-01',
      'Fecha de nacimiento debe ser posterior a 1900',
    )
    .optional()
    .or(z.literal('')),
  estado_civil: z
    .enum(['soltero', 'casado', 'divorciado', 'viudo', 'conviviente'])
    .nullable()
    .optional(),

  // Domicilio
  domicilio: z
    .string()
    .trim()
    .max(300, 'Domicilio no puede superar 300 caracteres')
    .optional()
    .or(z.literal('')),
  provincia_id: z.string().uuid().nullable().optional(),
  localidad_id: z.string().uuid().nullable().optional(),

  // Contacto
  telefono: z
    .string()
    .trim()
    .max(50, 'Teléfono no puede superar 50 caracteres')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Email no tiene un formato válido')
    .max(120, 'Email no puede superar 120 caracteres')
    .optional()
    .or(z.literal('')),

  // Afiliación
  fecha_alta_sistema: z
    .string()
    .refine(
      (v) => v === '' || v <= hoyIso(),
      'Fecha de alta no puede ser futura',
    )
    .optional()
    .or(z.literal('')),
  estado: z.enum(['activo', 'inactivo', 'baja']),
  observaciones: z
    .string()
    .trim()
    .max(1000, 'Observaciones no pueden superar 1000 caracteres')
    .optional()
    .or(z.literal('')),
});

export type AfiliadoFormValues = z.infer<typeof afiliadoFormSchema>;

export const defaultAfiliadoValues: AfiliadoFormValues = {
  cuil: '',
  tipo_documento: 'DNI',
  numero_documento: '',
  apellido: '',
  nombre: '',
  sexo: undefined,
  fecha_nacimiento: '',
  estado_civil: undefined,
  domicilio: '',
  provincia_id: null,
  localidad_id: null,
  telefono: '',
  email: '',
  fecha_alta_sistema: '',
  estado: 'activo',
  observaciones: '',
};
